import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Divider,
  Stack,
  alpha
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Star as StarIcon,
  Warning as WarningIcon,
  Psychology as AIIcon,
  EmojiEvents as TrophyIcon,
  Refresh as RefreshIcon,
  AutoAwesome as SparkleIcon,
  Insights as InsightsIcon,
  Speed as SpeedIcon,
  Groups as GroupsIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useSelector } from 'react-redux';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const PerformanceDashboard = () => {
  const userMeta = localStorage.getItem('login_meta');
  const { companyCode, branchId } = userMeta ? JSON.parse(userMeta) : {};

  const [analytics, setAnalytics] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState('THIS_YEAR');
  const [employeeMap, setEmployeeMap] = useState({});
  const [topSlideIndex, setTopSlideIndex] = useState(0);

  const ratingColors = {
    OUTSTANDING: '#4caf50',
    EXCEEDS_EXPECTATIONS: '#8bc34a',
    MEETS_EXPECTATIONS: '#2196f3',
    NEEDS_IMPROVEMENT: '#ff9800',
    UNSATISFACTORY: '#f44336'
  };

  useEffect(() => {
    fetchAnalytics();
    fetchRecentReviews();
  }, [dateRange]);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await axios.get('/employee/getAllEmployees');
        console.log('📊 Performance Dashboard - Fetched employees:', response.data?.length || 0);
        if (Array.isArray(response.data)) {
          const map = {};
          response.data.forEach(emp => {
            if (emp && emp._id) {
              map[emp._id] = emp;
              // Log first employee with image for verification
              if (Object.keys(map).length === 1 && emp.imageUrl) {
                console.log('✅ Sample employee image URL:', emp.imageUrl);
                console.log('   Employee:', emp.staff_name, '(ID:', emp.emp_id, ')');
              }
            }
          });
          setEmployeeMap(map);
          console.log('✅ Employee map created with', Object.keys(map).length, 'employees');
        }
      } catch (error) {
        console.error('❌ Error fetching employees for performance dashboard:', error);
      }
    };

    fetchEmployees();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const dates = getDateRange(dateRange);
      const response = await axios.post('/api/employee-performance/analytics', dates);
      if (response.data.success) {
        setAnalytics(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentReviews = async () => {
    try {
      const dates = getDateRange(dateRange);
      const response = await axios.post('/api/employee-performance/review/list', {
        ...dates,
        page: 1,
        limit: 10
      });
      if (response.data.success) {
        setReviews(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const getDateRange = (range) => {
    const now = new Date();
    let startDate, endDate;

    switch (range) {
      case 'THIS_MONTH':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'THIS_QUARTER':
        const quarter = Math.floor(now.getMonth() / 3);
        startDate = new Date(now.getFullYear(), quarter * 3, 1);
        endDate = new Date(now.getFullYear(), (quarter + 1) * 3, 0);
        break;
      case 'THIS_YEAR':
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = new Date(now.getFullYear(), 11, 31);
        break;
      default:
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = new Date(now.getFullYear(), 11, 31);
    }

    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  };

  const getRatingIcon = (rating) => {
    switch (rating) {
      case 'OUTSTANDING':
        return <TrophyIcon sx={{ color: ratingColors.OUTSTANDING }} />;
      case 'EXCEEDS_EXPECTATIONS':
        return <StarIcon sx={{ color: ratingColors.EXCEEDS_EXPECTATIONS }} />;
      case 'MEETS_EXPECTATIONS':
        return <TrendingFlatIcon sx={{ color: ratingColors.MEETS_EXPECTATIONS }} />;
      case 'NEEDS_IMPROVEMENT':
        return <TrendingDownIcon sx={{ color: ratingColors.NEEDS_IMPROVEMENT }} />;
      case 'UNSATISFACTORY':
        return <WarningIcon sx={{ color: ratingColors.UNSATISFACTORY }} />;
      default:
        return null;
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'IMPROVING':
        return <TrendingUpIcon sx={{ color: '#4caf50' }} />;
      case 'DECLINING':
        return <TrendingDownIcon sx={{ color: '#f44336' }} />;
      default:
        return <TrendingFlatIcon sx={{ color: '#757575' }} />;
    }
  };

  // Prepare chart data
  const ratingDistributionData = analytics ? Object.entries(analytics.ratingDistribution).map(([key, value]) => ({
    name: key.replace(/_/g, ' '),
    value,
    color: ratingColors[key]
  })) : [];
  const hasReviews = analytics && analytics.totalReviews > 0;

   // Prepare trend-over-time data from recent reviews
  const trendData = useMemo(() => {
    if (!reviews || reviews.length === 0) return [];

    const grouped = {};
    reviews.forEach(review => {
      if (!review.reviewEndDate) return;
      const d = new Date(review.reviewEndDate);
      if (Number.isNaN(d.getTime())) return;
      const key = d.toISOString().split('T')[0]; // YYYY-MM-DD
      if (!grouped[key]) {
        grouped[key] = { date: key, total: 0, count: 0 };
      }
      grouped[key].total += review.achievementPercentage || 0;
      grouped[key].count += 1;
    });

    return Object.values(grouped)
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(item => ({
        date: item.date,
        averageScore: item.count > 0 ? Math.round((item.total / item.count) * 10) / 10 : 0
      }));
  }, [reviews]);

  const enhancedTopPerformers = useMemo(() => {
    if (!analytics || !Array.isArray(analytics.topPerformers)) return [];
    const enhanced = analytics.topPerformers.map((tp, index) => {
      const emp = employeeMap[tp.employeeId];
      const result = {
        ...tp,
        employeeName: emp?.staff_name || tp.employeeName,
        empCode: emp?.emp_id,
        imageUrl: emp?.imageUrl
      };
      
      // Log first top performer for verification
      if (index === 0) {
        console.log('🏆 Top Performer #1:', {
          name: result.employeeName,
          empId: result.empCode,
          hasImage: !!result.imageUrl,
          imageUrl: result.imageUrl || 'No image URL'
        });
      }
      
      return result;
    });
    
    return enhanced;
  }, [analytics, employeeMap]);

  useEffect(() => {
    if (topSlideIndex > 0 && topSlideIndex >= enhancedTopPerformers.length) {
      setTopSlideIndex(0);
    }
  }, [topSlideIndex, enhancedTopPerformers.length]);

  // Derive simple AI-style insights from analytics
  const dashboardInsights = (() => {
    if (!analytics || !hasReviews) {
      return {
        trendLabel: 'Not enough data',
        trendIcon: 'FLAT',
        trendText: 'Create performance reviews to unlock AI-powered insights.',
        riskLevel: 'N/A',
        riskText: 'Risk assessment will appear once you start recording reviews.',
        recommendationTitle: 'Start capturing performance data',
        recommendationText: 'Encourage managers to submit regular performance reviews for this period.'
      };
    }

    const { averageScore, ratingDistribution, pipCount, totalReviews } = analytics;
    const lowCount = (ratingDistribution.NEEDS_IMPROVEMENT || 0) + (ratingDistribution.UNSATISFACTORY || 0);
    const lowRatio = totalReviews > 0 ? lowCount / totalReviews : 0;
    const pipRatio = totalReviews > 0 ? (pipCount || 0) / totalReviews : 0;

    let trendIcon = 'FLAT';
    let trendLabel = 'Stable';
    let trendText = 'Performance is broadly stable with a mix of ratings.';

    if (averageScore >= 85 && lowRatio < 0.1) {
      trendIcon = 'UP';
      trendLabel = 'Improving';
      trendText = 'Strong overall performance with very few low ratings.';
    } else if (averageScore <= 65 || lowRatio > 0.3) {
      trendIcon = 'DOWN';
      trendLabel = 'Declining';
      trendText = 'Many employees are below expectations. Focus on coaching and support.';
    }

    let riskLevel = 'LOW';
    if (lowRatio > 0.3 || pipRatio > 0.25) {
      riskLevel = 'HIGH';
    } else if (lowRatio > 0.15 || pipRatio > 0.1) {
      riskLevel = 'MEDIUM';
    }

    let riskText;
    if (riskLevel === 'LOW') {
      riskText = 'Most employees are performing well with limited risk signals.';
    } else if (riskLevel === 'MEDIUM') {
      riskText = 'Some risk pockets detected. Monitor employees needing improvement closely.';
    } else {
      riskText = 'High concentration of low ratings. Immediate intervention is recommended.';
    }

    let recommendationTitle;
    let recommendationText;
    if (riskLevel === 'LOW') {
      recommendationTitle = 'Recognize and retain top talent';
      recommendationText = 'Celebrate high performers and share their best practices across the team.';
    } else if (riskLevel === 'MEDIUM') {
      recommendationTitle = 'Targeted coaching plan';
      recommendationText = 'Schedule check-ins and focused training for employees below expectations.';
    } else {
      recommendationTitle = 'Immediate performance action plan';
      recommendationText = 'Prioritize PIPs and close mentoring for underperforming employees.';
    }

    return {
      trendLabel,
      trendIcon,
      trendText,
      riskLevel,
      riskText,
      recommendationTitle,
      recommendationText
    };
  })();

  if (!analytics) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 3, 
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      minHeight: '100vh'
    }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          mb: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 3
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" fontWeight="700" sx={{ mb: 1 }}>
              Performance Intelligence Dashboard
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AIIcon fontSize="small" />
              AI-Powered Analytics & Insights
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl sx={{ minWidth: 200 }}>
              <Select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
                  '.MuiSvgIcon-root': { color: 'white' }
                }}
              >
                <MenuItem value="THIS_MONTH">This Month</MenuItem>
                <MenuItem value="THIS_QUARTER">This Quarter</MenuItem>
                <MenuItem value="THIS_YEAR">This Year</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title="Refresh Data">
              <IconButton 
                onClick={() => { fetchAnalytics(); fetchRecentReviews(); }}
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            elevation={0}
            sx={{ 
              height: '100%',
              borderRadius: 3,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -50,
                right: -50,
                width: 150,
                height: 150,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            <CardContent sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <GroupsIcon fontSize="small" />
                <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
                  Total Reviews
                </Typography>
              </Box>
              <Typography variant="h3" fontWeight="700" sx={{ mb: 'auto' }}>
                {analytics.totalReviews}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8, mt: 2, display: 'block', fontSize: '0.75rem' }}>
                Performance evaluations completed
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            elevation={0}
            sx={{ 
              height: '100%',
              borderRadius: 3,
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -50,
                right: -50,
                width: 150,
                height: 150,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            <CardContent sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <SpeedIcon fontSize="small" />
                <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
                  Average Score
                </Typography>
              </Box>
              <Typography variant="h3" fontWeight="700" sx={{ mb: 'auto' }}>
                {analytics.averageScore.toFixed(1)}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={analytics.averageScore}
                sx={{ 
                  mt: 2, 
                  height: 6, 
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.3)',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: 'white',
                    borderRadius: 3
                  }
                }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            elevation={0}
            sx={{ 
              height: '100%',
              borderRadius: 3,
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -50,
                right: -50,
                width: 150,
                height: 150,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            <CardContent sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <WarningIcon fontSize="small" />
                <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
                  Active PIPs
                </Typography>
              </Box>
              <Typography variant="h3" fontWeight="700" sx={{ mb: 'auto' }}>
                {analytics.pipCount}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8, mt: 2, display: 'block', fontSize: '0.75rem' }}>
                Employees under improvement plan
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            elevation={0}
            sx={{ 
              height: '100%',
              borderRadius: 3,
              background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -50,
                right: -50,
                width: 150,
                height: 150,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            <CardContent sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TrophyIcon fontSize="small" />
                <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
                  Top Performers
                </Typography>
              </Box>
              <Typography variant="h3" fontWeight="700" sx={{ mb: 'auto' }}>
                {analytics.topPerformers.length}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8, mt: 2, display: 'block', fontSize: '0.75rem' }}>
                Outstanding achievers
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Trend & Featured Top Performer */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <SparkleIcon color="primary" />
              <Typography variant="h6" fontWeight="600">
                Trend Over Time
              </Typography>
            </Box>
            {hasReviews && trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <RechartsTooltip />
                  <Line
                    type="monotone"
                    dataKey="averageScore"
                    stroke="#667eea"
                    strokeWidth={3}
                    dot={{ r: 4, fill: '#667eea' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Box
                sx={{
                  height: 260,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary',
                  fontSize: 14
                }}
              >
                No trend data available yet
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrophyIcon sx={{ color: '#FFD700', fontSize: 22 }} />
                <Typography variant="subtitle1" fontWeight="600">
                  Top Performer
                </Typography>
              </Box>
              {enhancedTopPerformers.length > 1 && (
                <Box>
                  <IconButton
                    size="small"
                    onClick={() => setTopSlideIndex(prev => Math.max(prev - 1, 0))}
                    disabled={topSlideIndex === 0}
                    sx={{ p: 0.5 }}
                  >
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setTopSlideIndex(prev => Math.min(prev + 1, enhancedTopPerformers.length - 1))}
                    disabled={topSlideIndex >= enhancedTopPerformers.length - 1}
                    sx={{ p: 0.5 }}
                  >
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
            {enhancedTopPerformers.length > 0 ? (
              <Box sx={{ flex: 1, minHeight: 0 }}>
                {(() => {
                  const performer = enhancedTopPerformers[topSlideIndex];
                  const fallbackGradient = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                  return (
                    <Box
                      sx={{
                        position: 'relative',
                        height: '100%',
                        minHeight: 320,
                        borderRadius: 2.5,
                        overflow: 'hidden',
                        backgroundImage: performer.imageUrl 
                          ? `url("${performer.imageUrl}")` 
                          : fallbackGradient,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        backgroundColor: '#667eea' // Fallback color if image fails
                      }}
                      onError={(e) => {
                        console.warn('⚠️ Failed to load image for:', performer.employeeName);
                        e.target.style.backgroundImage = fallbackGradient;
                      }}
                    >
                      {/* Dark overlay gradient */}
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.2) 100%)'
                        }}
                      />
                      
                      {/* Top badge */}
                      <Box sx={{ position: 'absolute', top: 12, left: 12, right: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Chip
                          icon={<StarIcon sx={{ fontSize: 16, color: 'white !important' }} />}
                          label={`#${topSlideIndex + 1} Performer`}
                          size="small"
                          sx={{ 
                            bgcolor: 'rgba(255, 215, 0, 0.95)',
                            color: '#000',
                            fontWeight: 700,
                            fontSize: '0.75rem'
                          }}
                        />
                        {enhancedTopPerformers.length > 1 && (
                          <Chip
                            label={`${topSlideIndex + 1}/${enhancedTopPerformers.length}`}
                            size="small"
                            sx={{ 
                              bgcolor: 'rgba(255,255,255,0.25)',
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              backdropFilter: 'blur(10px)'
                            }}
                          />
                        )}
                      </Box>

                      {/* Bottom info overlay */}
                      <Box sx={{ 
                        position: 'absolute', 
                        bottom: 0, 
                        left: 0, 
                        right: 0, 
                        p: 2,
                        background: 'linear-gradient(to top, rgba(0,0,0,0.95), transparent)',
                        color: 'white'
                      }}>
                        <Typography variant="h6" fontWeight="700" sx={{ mb: 0.5, lineHeight: 1.2 }}>
                          {performer.employeeName}
                        </Typography>
                        {performer.empCode && (
                          <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', mb: 1.5 }}>
                            Employee ID: {performer.empCode}
                          </Typography>
                        )}
                        
                        {/* Performance metrics */}
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 1.5, 
                          mb: 1,
                          flexWrap: 'wrap'
                        }}>
                          <Box sx={{ 
                            bgcolor: 'rgba(76, 175, 80, 0.25)',
                            backdropFilter: 'blur(10px)',
                            px: 1.5,
                            py: 0.75,
                            borderRadius: 1.5,
                            border: '1px solid rgba(76, 175, 80, 0.5)'
                          }}>
                            <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', fontSize: '0.65rem' }}>
                              Score
                            </Typography>
                            <Typography variant="body2" fontWeight="700" sx={{ fontSize: '1rem', color: '#4caf50' }}>
                              {performer.score?.toFixed(1)}%
                            </Typography>
                          </Box>
                          <Box sx={{ 
                            bgcolor: 'rgba(255, 215, 0, 0.25)',
                            backdropFilter: 'blur(10px)',
                            px: 1.5,
                            py: 0.75,
                            borderRadius: 1.5,
                            border: '1px solid rgba(255, 215, 0, 0.5)'
                          }}>
                            <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', fontSize: '0.65rem' }}>
                              Status
                            </Typography>
                            <Typography variant="body2" fontWeight="700" sx={{ fontSize: '0.85rem', color: '#FFD700' }}>
                              Outstanding
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Typography variant="caption" sx={{ 
                          opacity: 0.75, 
                          display: 'block',
                          fontSize: '0.7rem',
                          fontStyle: 'italic'
                        }}>
                          Consistently exceeding expectations
                        </Typography>
                      </Box>
                    </Box>
                  );
                })()}
              </Box>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary',
                  fontSize: 14,
                  minHeight: 320,
                  border: '2px dashed #e0e0e0',
                  borderRadius: 2.5
                }}
              >
                No top performers for this period
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Top Performing Department */}
      {hasReviews && (
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <GroupsIcon color="primary" />
            <Typography variant="h6" fontWeight="600">
              Top Performing Department
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {(() => {
              // Calculate department performance from reviews
              const deptPerformance = {};
              reviews.forEach(review => {
                const deptName = review.employeeId?.department?.departmentName || 'Unknown';
                if (!deptPerformance[deptName]) {
                  deptPerformance[deptName] = { total: 0, count: 0, name: deptName };
                }
                deptPerformance[deptName].total += review.achievementPercentage || 0;
                deptPerformance[deptName].count += 1;
              });

              const deptArray = Object.values(deptPerformance)
                .map(d => ({
                  ...d,
                  average: d.count > 0 ? d.total / d.count : 0
                }))
                .sort((a, b) => b.average - a.average)
                .slice(0, 4);

              if (deptArray.length === 0) {
                return (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" align="center">
                      No department data available
                    </Typography>
                  </Grid>
                );
              }

              return deptArray.map((dept, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      background: index === 0 
                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                        : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                      color: index === 0 ? 'white' : 'inherit',
                      position: 'relative',
                      overflow: 'hidden',
                      border: index === 0 ? 'none' : '1px solid #e0e0e0',
                      '&::before': index === 0 ? {
                        content: '""',
                        position: 'absolute',
                        top: -30,
                        right: -30,
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.1)'
                      } : {}
                    }}
                  >
                    {index === 0 && (
                      <TrophyIcon sx={{ 
                        position: 'absolute', 
                        top: 12, 
                        right: 12, 
                        color: '#FFD700',
                        fontSize: 28,
                        opacity: 0.9
                      }} />
                    )}
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        opacity: index === 0 ? 0.9 : 0.7,
                        display: 'block',
                        mb: 0.5,
                        fontWeight: 600
                      }}
                    >
                      {index === 0 ? '🏆 Top Department' : `#${index + 1} Department`}
                    </Typography>
                    <Typography 
                      variant="h6" 
                      fontWeight="700"
                      sx={{ mb: 1, fontSize: '1.1rem' }}
                    >
                      {dept.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                      <Typography variant="h4" fontWeight="700">
                        {dept.average.toFixed(1)}%
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: index === 0 ? 0.8 : 0.6 }}>
                        avg score
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ opacity: index === 0 ? 0.8 : 0.6, mt: 0.5, display: 'block' }}>
                      {dept.count} review{dept.count !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                </Grid>
              ));
            })()}
          </Grid>
        </Paper>
      )}

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Rating Distribution */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <InsightsIcon color="primary" />
              <Typography variant="h6" fontWeight="600">
                Rating Distribution
              </Typography>
            </Box>
            {hasReviews ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={ratingDistributionData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {ratingDistributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Box
                sx={{
                  height: 260,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary',
                  fontSize: 14
                }}
              >
                No reviews found for this period
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Performance Trend */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <SparkleIcon color="primary" />
              <Typography variant="h6" fontWeight="600">
                Performance Overview
              </Typography>
            </Box>
            <Box sx={{ mt: 2 }}>
              {Object.entries(analytics.ratingDistribution).map(([rating, count]) => (
                <Box key={rating} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getRatingIcon(rating)}
                      <Typography variant="body2">
                        {rating.replace(/_/g, ' ')}
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight="bold">
                      {count} ({analytics.totalReviews > 0 ? ((count / analytics.totalReviews) * 100).toFixed(1) : 0}%)
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={analytics.totalReviews > 0 ? (count / analytics.totalReviews) * 100 : 0}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      bgcolor: alpha(ratingColors[rating], 0.1),
                      '& .MuiLinearProgress-bar': {
                        bgcolor: ratingColors[rating],
                        borderRadius: 5
                      }
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Top and Low Performers */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Top Performers */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1, 
              mb: 3,
              pb: 2,
              borderBottom: '2px solid',
              borderColor: 'success.main'
            }}>
              <TrophyIcon sx={{ color: '#FFD700', fontSize: 28 }} />
              <Typography variant="h6" fontWeight="600">
                Top Performers
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell align="right">Score</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analytics.topPerformers.slice(0, 5).map((performer, index) => {
                    const employee = employeeMap[performer.employeeId];
                    return (
                    <TableRow key={index}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar 
                            src={employee?.imageUrl}
                            sx={{ 
                              width: 36, 
                              height: 36, 
                              bgcolor: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : index === 2 ? '#CD7F32' : '#4caf50',
                              fontWeight: 'bold',
                              border: index < 3 ? '2px solid' : 'none',
                              borderColor: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : '#CD7F32'
                            }}
                          >
                            {!employee?.imageUrl && (index + 1)}
                          </Avatar>
                          <Typography variant="body2">
                            {performer.employeeName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${performer.score.toFixed(1)}%`}
                          size="small"
                          color="success"
                        />
                      </TableCell>
                    </TableRow>
                    );
                  })}
                  {analytics.topPerformers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        No data available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Low Performers */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1, 
              mb: 3,
              pb: 2,
              borderBottom: '2px solid',
              borderColor: 'error.main'
            }}>
              <WarningIcon color="error" sx={{ fontSize: 28 }} />
              <Typography variant="h6" fontWeight="600">
                Needs Attention
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell align="right">Score</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analytics.lowPerformers.slice(0, 5).map((performer, index) => {
                    const employee = employeeMap[performer.employeeId];
                    return (
                    <TableRow key={index}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar 
                            src={employee?.imageUrl}
                            sx={{ 
                              width: 32, 
                              height: 32, 
                              bgcolor: '#f44336',
                              border: '2px solid #f44336'
                            }}
                          >
                            {!employee?.imageUrl && '!'}
                          </Avatar>
                          <Typography variant="body2">
                            {performer.employeeName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${performer.score.toFixed(1)}%`}
                          size="small"
                          color="error"
                        />
                      </TableCell>
                    </TableRow>
                    );
                  })}
                  {analytics.lowPerformers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        No data available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* AI Insights Section */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <AIIcon sx={{ color: '#667eea', fontSize: 28 }} />
          <Typography variant="h6" fontWeight="600">
            AI-Powered Insights
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, border: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Performance Trend
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                {dashboardInsights.trendIcon === 'UP' ? (
                  <TrendingUpIcon sx={{ color: '#4caf50' }} />
                ) : dashboardInsights.trendIcon === 'DOWN' ? (
                  <TrendingDownIcon sx={{ color: '#f44336' }} />
                ) : (
                  <TrendingFlatIcon sx={{ color: '#757575' }} />
                )}
                <Typography variant="h6" fontWeight="600">
                  {dashboardInsights.trendLabel}
                </Typography>
              </Box>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                {dashboardInsights.trendText}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, border: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Risk Assessment
              </Typography>
              <Chip 
                label={dashboardInsights.riskLevel === 'N/A' ? 'No Data' : `${dashboardInsights.riskLevel} Risk`}
                size="small" 
                color={
                  dashboardInsights.riskLevel === 'LOW'
                    ? 'success'
                    : dashboardInsights.riskLevel === 'MEDIUM'
                    ? 'warning'
                    : dashboardInsights.riskLevel === 'HIGH'
                    ? 'error'
                    : 'default'
                }
                sx={{ mt: 1, fontWeight: 600 }}
              />
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                {dashboardInsights.riskText}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, border: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Recommendation
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                {dashboardInsights.recommendationTitle}
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                {dashboardInsights.recommendationText}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Recent Reviews */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <InsightsIcon color="primary" />
          <Typography variant="h6" fontWeight="600">
            Recent Performance Reviews
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Review Period</TableCell>
                <TableCell>Score</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell>AI Insights</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reviews.map((review) => {
                const employee = employeeMap[review.employeeId?._id];
                return (
                <TableRow key={review._id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar 
                        src={employee?.imageUrl}
                        sx={{ 
                          bgcolor: 'primary.main',
                          width: 40,
                          height: 40
                        }}
                      >
                        {!employee?.imageUrl && review.employeeId?.staff_name?.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="600">
                          {review.employeeId?.staff_name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          ID: {review.employeeId?.emp_id}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{review.reviewPeriod}</TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5 }}>
                        {review.achievementPercentage.toFixed(1)}%
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={review.achievementPercentage}
                        sx={{ 
                          width: 120, 
                          height: 8,
                          borderRadius: 4,
                          bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            bgcolor: review.achievementPercentage >= 80 ? '#4caf50' : review.achievementPercentage >= 60 ? '#ff9800' : '#f44336'
                          }
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getRatingIcon(review.rating)}
                      <Chip
                        label={review.rating.replace(/_/g, ' ')}
                        size="small"
                        sx={{
                          bgcolor: ratingColors[review.rating],
                          color: 'white'
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    {review.aiInsights && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getTrendIcon(review.aiInsights.performanceTrend)}
                        <Chip
                          label={review.aiInsights.riskLevel}
                          size="small"
                          color={
                            review.aiInsights.riskLevel === 'LOW' ? 'success' :
                            review.aiInsights.riskLevel === 'MEDIUM' ? 'warning' : 'error'
                          }
                        />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(review.reviewEndDate).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              );
              })}
              {reviews.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No recent reviews
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      </Box>
    </Box>
  );
};

export default PerformanceDashboard;
